import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SystemStatusProvider, useSystemStatus } from './context/SystemStatusContext';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import InterviewPage from './pages/InterviewPage';
import InterviewReportPage from './pages/InterviewReportPage';
import NotificationsPage from './pages/NotificationsPage';
import ClassicQuestionsPage from './pages/ClassicQuestionsPage';
import TicketList from './pages/TicketList';
import TicketCreate from './pages/TicketCreate';
import TicketDetail from './pages/TicketDetail';
import AdminLogin from './pages/AdminLogin';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import WorkplaceNewsDetailPage from './pages/WorkplaceNewsDetailPage.jsx';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Components
import Navbar from './components/common/Navbar';
import UnifiedLoginModal from './components/common/UnifiedLoginModal';
import AlertModal from './components/common/AlertModal';
import ServiceUnavailable from './components/common/ServiceUnavailable';
import JobDetail from './components/dashboard/JobDetail';
import QuestionDetail from './components/dashboard/QuestionDetail';
import AdminLayout from './components/admin/AdminLayout.jsx';
import BrowserCompatibilityChecker from './components/BrowserCompatibilityChecker';
import ResumeTemplateDetail from './components/dashboard/ResumeTemplateDetail';

const BAIDU_TONGJI_ID = process.env.REACT_APP_BAIDU_TONGJI_ID;

function initBaiduTongji() {
  if (typeof window === 'undefined') {
    return;
  }
  if (!BAIDU_TONGJI_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[百度统计] 未配置 REACT_APP_BAIDU_TONGJI_ID');
    }
    return;
  }
  if (window.__baiduTongjiInitialized) {
    return;
  }
  
  // 初始化 _hmt 数组（必须在脚本加载前初始化）
  window._hmt = window._hmt || [];
  
  const hm = document.createElement('script');
  hm.src = `https://hm.baidu.com/hm.js?${BAIDU_TONGJI_ID}`;
  hm.async = true;
  
  // 标记脚本加载完成
  hm.onload = () => {
    window.__baiduTongjiReady = true;
    if (process.env.NODE_ENV === 'development') {
      console.log('[百度统计] 脚本加载成功，ID:', BAIDU_TONGJI_ID);
    }
  };
  
  hm.onerror = () => {
    console.error('[百度统计] 脚本加载失败，请检查网络连接和统计ID');
    window.__baiduTongjiReady = false;
  };
  
  // 按照百度统计标准：优先插入到 head 标签的末尾（在 </head> 之前）
  // 如果 head 不存在，则插入到第一个 script 标签之前
  const head = document.getElementsByTagName('head')[0];
  
  if (head) {
    // 优先插入到 head 标签末尾（符合百度统计标准）
    head.appendChild(hm);
  } else {
    // 如果 head 不存在，插入到第一个 script 标签之前（百度统计标准做法）
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(hm, firstScript);
    } else {
      // 如果连 script 都没有，等待 DOM 加载完成
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          const head = document.getElementsByTagName('head')[0];
          if (head) {
            head.appendChild(hm);
          } else {
            const firstScript = document.getElementsByTagName('script')[0];
            if (firstScript && firstScript.parentNode) {
              firstScript.parentNode.insertBefore(hm, firstScript);
            }
          }
        });
      }
    }
  }
  
  window.__baiduTongjiInitialized = true;
}

function trackBaiduPageView(path) {
  if (typeof window === 'undefined') {
    return;
  }
  
  if (!BAIDU_TONGJI_ID) {
    return;
  }
  
  // 确保 _hmt 数组存在
  window._hmt = window._hmt || [];
  
  // 如果脚本已加载完成，直接跟踪
  if (window.__baiduTongjiReady) {
    window._hmt.push(['_trackPageview', path]);
    return;
  }
  
  // 如果脚本还在加载，将跟踪命令加入队列
  // 百度统计脚本加载完成后会自动执行队列中的命令
  window._hmt.push(['_trackPageview', path]);
  
  // 如果脚本加载超时（超过5秒），尝试直接跟踪
  if (!window.__baiduTongjiTimeout) {
    window.__baiduTongjiTimeout = setTimeout(() => {
      if (window._hmt && Array.isArray(window._hmt)) {
        // 再次尝试跟踪，即使脚本可能还没完全加载
        window._hmt.push(['_trackPageview', path]);
      }
    }, 5000);
  }
}

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.5;
  }

  html {
    scroll-behavior: smooth;
  }

  button {
    font-family: inherit;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  /* Loading animation */
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 18px;
`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <LoadingContainer>
        <div>加载中...</div>
      </LoadingContainer>
    );
  }

  return isAuthenticated ? children : <Navigate to="/" />;
};

// Admin Protected Route Component - 管理员路由保护
const AdminProtectedRoute = ({ children }) => {
  const [isVerifying, setIsVerifying] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const verifyAdmin = async () => {
      const token = localStorage.getItem('admin_token');
      const adminUser = localStorage.getItem('admin_user');

      if (!token || !adminUser) {
        setIsVerifying(false);
        setIsAdmin(false);
        return;
      }

      try {
        const userData = JSON.parse(adminUser);
        if (userData && userData.role && (userData.role === 'operation_admin' || userData.role === 'support_admin')) {
          setIsAdmin(true);
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          setIsAdmin(false);
        }
      }
      catch (error) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        setIsAdmin(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAdmin();
  }, []);

  if (isVerifying) {
    return (
      <LoadingContainer>
        <div>验证管理员身份...</div>
      </LoadingContainer>
    );
  }

  if (!isAdmin) {
    // 跳转到管理员登录页
    return <Navigate to="/sys-portal/login" replace />;
  }

  return children;
};

const InviteRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
};

function AppContent() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [authErrorModal, setAuthErrorModal] = useState({ isOpen: false, message: '' });
  const { isAuthenticated } = useAuth();
  const { isHealthy, status } = useSystemStatus();
  const location = useLocation();

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleCloseAuthErrorModal = () => {
    setAuthErrorModal({ isOpen: false, message: '' });
    // 关闭错误提示后，打开登录弹窗
    if (window.location.pathname !== '/sys-portal/login') {
      setIsLoginModalOpen(true);
    }
  };

  // 监听认证错误事件，显示错误提示并打开登录弹窗
  useEffect(() => {
    const handleAuthError = (event) => {
      const message = event.detail?.message || '登录已过期，请重新登录';
      // 防止重复显示弹框
      setAuthErrorModal((prev) => {
        // 如果弹框已经打开，不再重复打开
        if (prev.isOpen) {
          return prev;
        }
        return { isOpen: true, message };
      });
    };

    window.addEventListener('authError', handleAuthError);

    return () => {
      window.removeEventListener('authError', handleAuthError);
    };
  }, []);

  // 当用户登录成功后，自动跳转到仪表盘
  useEffect(() => {
    if (isAuthenticated && window.location.pathname === '/') {
      // 使用 setTimeout 确保状态更新完成后再跳转
      setTimeout(() => {
        window.location.href = '/app';
      }, 100);
    }
  }, [isAuthenticated]);

  // 页面浏览跟踪（路由变化时）
  useEffect(() => {
    const path = `${location.pathname}${location.search || ''}`;
    
    // 延迟一下确保脚本已经初始化
    const timer = setTimeout(() => {
      trackBaiduPageView(path);
      if (process.env.NODE_ENV === 'development') {
        console.log('[百度统计] 路由变化跟踪:', path);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // 监听用户活动，在 access_token 即将过期时尝试静默刷新
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let checkInterval = null;
    let lastCheckTime = 0; // 记录上次检查时间，避免频繁检查

    // 检查token是否即将过期并尝试刷新
    const checkAndRefreshToken = async () => {
      try {
        // 避免频繁检查：距离上次检查不足30秒则跳过
        const now = Date.now();
        if (now - lastCheckTime < 30000) {
          return;
        }
        lastCheckTime = now;

        const token = localStorage.getItem('access_token');
        if (!token) {
          // 没有token但状态是已认证，可能是其他标签页登出了
          // 不主动跳转，等待下次API调用时由拦截器处理
          return;
        }

        // 解析JWT token获取过期时间
        let payload;
        try {
          // 验证token格式（JWT应该有3部分，用.分隔）
          const parts = token.split('.');
          if (parts.length !== 3) {
            throw new Error('Invalid token format');
          }
          payload = JSON.parse(atob(parts[1]));
          
          if (!payload || typeof payload.exp !== 'number') {
            throw new Error('Invalid token payload');
          }
        } catch (error) {
          // token格式错误或解析失败，记录日志但不立即退出
          // 让下次API调用时由拦截器处理，避免误判
          console.warn('Token解析失败（将在下次API调用时处理）:', error);
          return;
        }

        const exp = payload.exp * 1000; // 转换为毫秒
        const timeUntilExpiry = exp - now;

        // 如果token已经过期，尝试刷新
        if (timeUntilExpiry <= 0) {
          // token已过期，但不立即退出登录，先尝试刷新
          // 如果刷新失败，由下次API调用的拦截器处理
          const { tryRefreshToken } = await import('./utils/authErrorHandler');
          await tryRefreshToken();
          return;
        }

        // 如果token还剩不到5分钟就过期，静默刷新
        // 刷新失败时不退出登录，让用户继续使用，由API拦截器处理后续401
        if (timeUntilExpiry < 5 * 60 * 1000) {
          const { tryRefreshToken } = await import('./utils/authErrorHandler');
          const refreshSuccess = await tryRefreshToken();
          
          if (refreshSuccess) {
            console.log('Token静默刷新成功');
          } else {
            console.warn('Token静默刷新失败（将在下次API调用时重试）');
          }
        }
      } catch (error) {
        // 获取token失败或其他错误，记录但不影响用户使用
        console.error('Token检查失败:', error);
      }
    };

    // 定期检查token（每3分钟检查一次，比access_token的30分钟有效期短）
    // 不再监听用户活动，避免频繁检查影响性能
    checkInterval = setInterval(() => {
      checkAndRefreshToken();
    }, 3 * 60 * 1000);

    // 初始检查（延迟5秒，避免页面加载时的性能影响）
    setTimeout(() => {
      checkAndRefreshToken();
    }, 5000);

    return () => {
      // 清理定时器
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [isAuthenticated]);

  // 如果系统不可用，显示服务不可用页面
  if (!isHealthy && status === 'unhealthy') {
    return <ServiceUnavailable />;
  }

  return (
    <AppContainer>
      <Toaster position="top-center" />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <HomePage onLoginClick={handleLoginClick} />
          </>
        } />

        <Route path="/invite" element={<InviteRedirect />} />

        {/* Classic Questions Public Route */}
        <Route path="/classic-questions" element={
          <>
            <ClassicQuestionsPage />
            <UnifiedLoginModal
              isOpen={isLoginModalOpen}
              onClose={handleCloseModal}
            />
          </>
        } />

        {/* Workplace News Detail Public Route */}
        <Route path="/workplace-news/:id" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <WorkplaceNewsDetailPage />
            <UnifiedLoginModal
              isOpen={isLoginModalOpen}
              onClose={handleCloseModal}
            />
          </>
        } />

        {/* Privacy and Terms Public Routes */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />

        {/* Reset Password Public Route */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected Routes */}
        {/* Unified workspace root (/app) with resource-oriented paths */}
        <Route path="/app" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />

        {/* Jobs */}
        {/* <Route path="/app/jobs/search" element={
          <ProtectedRoute>
            <DashboardPage activeView="job-assistant" />
          </ProtectedRoute>
        } /> */}
        <Route path="/app/jobs/:jobId" element={
          <ProtectedRoute>
            <JobDetail />
          </ProtectedRoute>
        } />

        {/* Resume Templates */}
        <Route path="/app/resume-templates" element={
          <ProtectedRoute>
            <DashboardPage activeView="resume-templates" />
          </ProtectedRoute>
        } />

        <Route path="/app/resume-templates/:id" element={
          <ProtectedRoute>
            <DashboardPage activeView="resume-templates">
              <ResumeTemplateDetail />
            </DashboardPage>
          </ProtectedRoute>
        } />

        {/* Resumes */}
        <Route path="/app/resumes/diagnosis" element={
          <ProtectedRoute>
            <DashboardPage activeView="resume-diagnosis" />
          </ProtectedRoute>
        } />

        {/* Interviews */}
        <Route path="/app/interviews/prep" element={
          <ProtectedRoute>
            <DashboardPage activeView="interview-prep" />
          </ProtectedRoute>
        } />
        <Route path="/app/interviews/mock" element={
          <ProtectedRoute>
            <DashboardPage activeView="mock-interview" />
          </ProtectedRoute>
        } />
        <Route path="/app/interviews/session" element={
          <ProtectedRoute>
            <InterviewPage />
          </ProtectedRoute>
        } />
        <Route path="/app/interviews/report/:interviewId" element={
          <ProtectedRoute>
            <InterviewReportPage />
          </ProtectedRoute>
        } />

        {/* Question details */}
        <Route path="/app/questions/:id" element={
          <ProtectedRoute>
            <QuestionDetail />
          </ProtectedRoute>
        } />

        {/* Meetings */}
        <Route path="/app/meetings" element={
          <ProtectedRoute>
            <DashboardPage activeView="meeting-assistant" />
          </ProtectedRoute>
        } />
        <Route path="/app/meetings/session" element={
          <ProtectedRoute>
            <DashboardPage activeView="meeting-assistant" />
          </ProtectedRoute>
        } />

        {/* Pricing */}
        <Route path="/app/pricing" element={
          <ProtectedRoute>
            <DashboardPage activeView="pricing" />
          </ProtectedRoute>
        } />

        {/* Notifications */}
        <Route path="/app/notifications" element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } />

        {/* Settings */}
        <Route path="/app/settings" element={
          <ProtectedRoute>
            <DashboardPage activeView="setting" />
          </ProtectedRoute>
        } />

        {/* Promotion & Support */}
        <Route path="/app/coop-promotion" element={
          <ProtectedRoute>
            <DashboardPage activeView="coop-promotion" />
          </ProtectedRoute>
        } />
        <Route path="/app/ticket-support" element={
          <ProtectedRoute>
            <DashboardPage activeView="ticket-support" />
          </ProtectedRoute>
        } />

        {/* Tickets as dedicated resource */}
        <Route path="/app/tickets" element={
          <ProtectedRoute>
            <TicketList />
          </ProtectedRoute>
        } />
        <Route path="/app/tickets/create" element={
          <ProtectedRoute>
            <TicketCreate />
          </ProtectedRoute>
        } />
        <Route path="/app/tickets/:id" element={
          <ProtectedRoute>
            <TicketDetail />
          </ProtectedRoute>
        } />



        {/* Admin Routes - Obfuscated Path with Protection */}
        <Route path="/sys-portal/login" element={<AdminLogin />} />
        <Route path="/sys-portal" element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        } />
        <Route path="/sys-portal/*" element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        } />

        {/* Legacy routes with navbar */}
        <Route path="/chat" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '80px', padding: '2rem', textAlign: 'center' }}>
              <h1>Search</h1>
              <p>请登录后使用完整功能</p>
            </div>
          </>
        } />

        <Route path="/resume" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        <Route path="/interview" element={<Navigate to="/classic-questions" replace />} />

        <Route path="/mock-interview" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        <Route path="/interview-prep" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        <Route path="/meeting-assistant" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* Unified Login Modal */}
      <UnifiedLoginModal
        isOpen={isLoginModalOpen}
        onClose={handleCloseModal}
      />

      {/* 认证错误提示弹窗 */}
      <AlertModal
        isOpen={authErrorModal.isOpen}
        onClose={handleCloseAuthErrorModal}
        title="登录已过期"
        message={authErrorModal.message}
        type="warning"
        confirmText="去登录"
        onConfirm={handleCloseAuthErrorModal}
      />
    </AppContainer>
  );
}

function App() {
  useEffect(() => {
    try {
      const migrate = (oldKey, newKey) => {
        const val = localStorage.getItem(oldKey);
        if (val && !localStorage.getItem(newKey)) {
          localStorage.setItem(newKey, val);
        }
        localStorage.removeItem(oldKey);
      };
      const migrateSS = (oldKey, newKey) => {
        const val = sessionStorage.getItem(oldKey);
        if (val && !sessionStorage.getItem(newKey)) {
          sessionStorage.setItem(newKey, val);
        }
        sessionStorage.removeItem(oldKey);
      };

      migrate('jobAssistantJobResults', 'searchResults');
      migrate('jobAssistantSearchQuery', 'searchQuery');
      migrate('jobAssistantLastUserId', 'searchLastUserId');
      migrate('jobAssistantLastVisit', 'searchLastVisit');

      migrateSS('jobAssistantViewState', 'searchViewState');
    } catch { }
  }, []);

  useEffect(() => {
    // 立即初始化百度统计（不等待 React 渲染完成）
    initBaiduTongji();
    
    // 脚本加载完成后，跟踪初始页面
    const checkAndTrack = () => {
      if (window.__baiduTongjiReady && window.location) {
        const path = `${window.location.pathname}${window.location.search || ''}`;
        trackBaiduPageView(path);
        if (process.env.NODE_ENV === 'development') {
          console.log('[百度统计] 初始页面跟踪:', path);
        }
      }
    };
    
    // 定期检查脚本是否加载完成（最多等待10秒）
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      if (window.__baiduTongjiReady || checkCount > 20) {
        clearInterval(checkInterval);
        if (window.__baiduTongjiReady) {
          checkAndTrack();
        } else if (process.env.NODE_ENV === 'development') {
          console.warn('[百度统计] 脚本加载超时，但会继续尝试跟踪');
        }
      }
    }, 500);
    
    return () => clearInterval(checkInterval);
  }, []);

  return (
    <HelmetProvider>
      <SystemStatusProvider>
        <AuthProvider>
          <BrowserCompatibilityChecker>
            <Router>
              <GlobalStyle />
              <AppContent />
            </Router>
          </BrowserCompatibilityChecker>
        </AuthProvider>
      </SystemStatusProvider>
    </HelmetProvider>
  );
}

export default App;
